import { Button } from '@timelyapp/react';
import classNames from 'classnames';
import Input from 'components/Input';
import InputLabel from 'components/InputLabel';
import React, { useEffect, useRef, useState } from 'react';
import sharedStyles from 'routes/auth/AuthPage.module.scss';
import AuthPageLayout from 'routes/auth/AuthPageLayout';
import OrDivider from 'routes/auth/OrDivider';
import SsoButtons from 'routes/auth/SsoButtons';
import { getBrowserMeta } from 'utils/browser-detection';
import { isElectronEnv } from 'utils/electron';
import styles from './LoginRoute.module.scss';

const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;

// The deprecated desktop app can no longer complete login (TT-5252). When the
// login page is rendered inside it, point users to the web-app setup guide.
const DESKTOP_APP_GUIDE_URL =
  'https://www.timely.com/help/handbook/getting-started/install-timely-web-app-mac/';

const isDesktopApp = () => isElectronEnv() || getBrowserMeta().isDesktopApp;

const LoginRoute: React.FC = () => {
  const [emailInput, setEmailInput] = useState(window.BOOTSTRAPPED_DATA.last_email || '');
  const [passwordInput, setPasswordInput] = useState('');
  const hasSsoCheck = !!window.BOOTSTRAPPED_DATA.sso_check_url;
  const [showPassword, setShowPassword] = useState(!hasSsoCheck);
  const [error, setError] = useState(window.BOOTSTRAPPED_DATA.flash_alert || '');
  const [checking, setChecking] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  // Move focus to password when it transitions visible (after Next or autofill).
  // Skip the initial render so the email input keeps autofocus on first paint.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (showPassword) passwordRef.current?.focus();
  }, [showPassword]);

  const revealPassword = () => setShowPassword(true);

  const runSsoCheck = async (email: string): Promise<'submit' | 'reveal'> => {
    if (!window.BOOTSTRAPPED_DATA.sso_check_url) return 'reveal';
    try {
      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
      const response = await fetch(window.BOOTSTRAPPED_DATA.sso_check_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-CSRF-Token': csrfToken || '',
        },
        body: `email=${encodeURIComponent(email)}`,
      });
      if (response.ok) {
        const data = await response.json();
        if (data.active) return 'submit';
      }
    } catch {
      // fall through to reveal
    }
    return 'reveal';
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    const email = emailInput.trim();

    if (!email || !EMAIL_REGEX.test(email)) {
      e.preventDefault();
      setError('Invalid email');
      return;
    }

    // Password already visible (or autofilled): let the form submit natively
    // so the browser's password manager sees a real submission and offers to
    // save the credentials.
    if (showPassword || passwordInput) return;

    e.preventDefault();
    setError('');
    setChecking(true);
    const result = await runSsoCheck(email);
    setChecking(false);
    if (result === 'submit') {
      formRef.current?.submit();
    } else {
      setShowPassword(true);
    }
  };

  // Detect autofill: when the hidden password gains a value without the user
  // having clicked Next, reveal the field so the credential is visible and
  // the submit button reads "Log in".
  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    setPasswordInput(value);
    if (value) revealPassword();
  };

  // Backstop for Webkit/Chromium: :-webkit-autofill triggers a CSS animation
  // before onChange fires. The matching keyframe is in LoginRoute.module.scss.
  const handlePasswordAnimationStart = (event: React.AnimationEvent<HTMLInputElement>) => {
    if (event.animationName === styles.onAutoFillStart) revealPassword();
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmailInput(event.currentTarget.value);
    if (error) setError('');
  };

  return (
    <AuthPageLayout
      title="Log in to Timely"
      bottomLink={
        <>
          Don&rsquo;t have an account? &nbsp;{' '}
          <a href={`${window.BOOTSTRAPPED_DATA.host}/join`} data-testid="signup_link">
            Sign up
          </a>
        </>
      }
    >
      {isDesktopApp() && (
        <div
          className={styles.deprecationNotice}
          role="status"
          data-testid="desktop_deprecation_notice"
        >
          <p>
            The Timely desktop app is no longer supported. You can keep using Timely in your
            browser.
          </p>
          <a href={DESKTOP_APP_GUIDE_URL} target="_blank" rel="noopener noreferrer">
            Set up the Timely web app
          </a>
        </div>
      )}
      <SsoButtons action="Log in" testIdPrefix="signin" />
      <OrDivider label="or" />
      <div className={sharedStyles.formInputs}>
        <form ref={formRef} id="login-form" action="/login" method="post" onSubmit={handleSubmit}>
          <input
            type="hidden"
            name="authenticity_token"
            value={window.BOOTSTRAPPED_DATA.authenticity_token}
          />
          <input
            type="hidden"
            name="user[client_id]"
            value={window.BOOTSTRAPPED_DATA.client_id || ''}
          />

          <div className={sharedStyles.inputContainer}>
            <Input
              className="fs-mask"
              type="email"
              name="user[email]"
              autoComplete="username"
              value={emailInput}
              onChange={handleEmailChange}
              data-testid="email_input"
              autoFocus
            />
            <InputLabel className={classNames({ [sharedStyles.activeLabel]: emailInput })}>
              Email
            </InputLabel>
          </div>

          <div
            className={classNames(sharedStyles.inputContainer, {
              [styles.passwordHidden]: !showPassword,
            })}
            aria-hidden={!showPassword}
          >
            <Input
              ref={passwordRef}
              className={classNames('fs-mask', styles.passwordInput)}
              type="password"
              name="user[password]"
              autoComplete="current-password"
              value={passwordInput}
              onChange={handlePasswordChange}
              onAnimationStart={handlePasswordAnimationStart}
              data-testid="password_input"
              tabIndex={showPassword ? 0 : -1}
            />
            <InputLabel className={classNames({ [sharedStyles.activeLabel]: passwordInput })}>
              Password
            </InputLabel>
          </div>

          {showPassword ? (
            <Button
              data-auth="memory"
              type="submit"
              className={sharedStyles.submitButton}
              priority="xhigh"
              size="large"
              data-testid="log_in_button"
            >
              Log in
            </Button>
          ) : (
            <Button
              type="submit"
              className={sharedStyles.submitButton}
              priority="xhigh"
              size="large"
              disabled={checking}
              data-testid="next_button"
            >
              {checking ? 'Checking...' : 'Next'}
            </Button>
          )}

          {error && (
            <div className={styles.error} data-testid="login_error" role="alert">
              {error}
            </div>
          )}

          <p className={styles.forgotPassword}>
            <a
              href={window.BOOTSTRAPPED_DATA.forgot_password_url}
              data-testid="forgot_password_link"
            >
              Forgot password?
            </a>
          </p>
        </form>
      </div>
    </AuthPageLayout>
  );
};

export default LoginRoute;
